import { useRef, useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';
import {
  HeroActionRail,
  railScheduleDef,
  railResidentDef,
  railApplyDef,
  railTourDef,
  railSelfGuidedDef,
} from './HeroActionRail';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. Falls back to image when empty.',
    },
    image: {
      label: 'Fallback / Poster Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  useCtaSheenStyles();

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  return (
    <section ref={sectionRef} className="hero-section hero-rail-anchor relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {showVideoBackground && videoInfo ? (
          <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
            {bgImage.image && (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoInfo.type === 'embed'
                    ? iframeLoaded
                      ? 'opacity-0'
                      : 'opacity-100'
                    : videoReady
                      ? 'opacity-0'
                      : 'opacity-100'
                }`}
              />
            )}
            {videoInfo.type === 'embed' ? (
              <iframe
                src={videoInfo.src}
                className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                  iframeLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                allow="autoplay; fullscreen"
                frameBorder="0"
                onLoad={() => setIframeLoaded(true)}
                title="Background video"
              />
            ) : (
              <video
                ref={videoRef}
                src={videoInfo.src}
                autoPlay
                muted
                loop
                playsInline
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoReady ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
          </div>
        ) : (
          /* Parallax + Ken Burns animated background */
          <div
            ref={imgWrapRef}
            className="hero-bg-media absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className="h-full w-full object-cover"
                style={{
                  animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="h-full w-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        )}
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content — left-aligned stack; extra right padding on lg+ reserves
          room for the action rail pinned to the right edge. */}
      <div className="hero-content relative z-10 flex h-full flex-col items-start justify-center px-6 sm:px-8 text-left text-white">
        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue="<p>In The Edge District</p>"
          as="h2"
          className="hero-entrance hero-entrance-1 hero-subtitle"
        />

        <RichTextContent
          path="hero.title"
          label="Title"
          defaultField="property_name"
          defaultValue="<p>Property Name</p>"
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta vantage-cta-sheen group/cta inline-flex items-center justify-center"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <HeroActionRail />

      {/* Schema-lint anchors — the interactive rail wrappers live in
          <HeroActionRail>; these hidden anchors keep the same-file wrapper
          check satisfied for the rail_* slot groups declared below. */}
      <EditableSlotGroup groupPath="hero.rail_schedule" definition={railScheduleDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_resident" definition={railResidentDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_apply" definition={railApplyDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_tour" definition={railTourDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_self_guided" definition={railSelfGuidedDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Full viewport height — svh/dvh for mobile browser chrome. */
        .hero-section {
          /* Contain the hero-content (z-10) and action rail (z-20) in their own
             stacking context so the floating rail buttons don't out-stack
             fixed-position third-party widgets (e.g. chatbots) at the page root. */
          isolation: isolate;
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          height: 100vh;
          height: 100svh;
          height: 100dvh;
          min-height: 100vh;
          min-height: 100svh;
          min-height: 100dvh;
        }
        @supports (height: 100dvh) {
          .hero-section {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        /* Hero copy — Cambria’s long property name needs more width than the
           parent’s 14ch stack, plus top clearance so copy clears the logo. */
        .hero-content {
          padding-left: 1.5rem;
          padding-top: 6.75rem;
          padding-bottom: 2rem;
          box-sizing: border-box;
        }
        @media (min-width: 768px) {
          .hero-content {
            padding-left: 4.4rem;
            padding-top: 8.5rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-content {
            padding-right: var(--hero-rail-width);
            padding-top: 9.5rem;
          }
        }
        .hero-title {
          font-family: "Raleway", sans-serif;
          font-size: 38px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          /* Wider than parent 14ch so the two-line name doesn’t wrap further. */
          max-width: min(28ch, 100%);
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-title { font-size: 68px; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 80px; }
        }
        @media (min-width: 1280px) {
          .hero-title { font-size: 95px; }
        }
        .hero-subtitle {
          font-family: "Raleway", sans-serif;
          font-size: 20px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0 0 0.35em;
          max-width: min(48ch, 100%);
        }
        .hero-subtitle p,
        .hero-subtitle span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          letter-spacing: inherit;
          margin: 0;
        }
        .hero-cta {
          font-family: "Raleway", sans-serif;
          font-size: 16px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0;
          text-transform: uppercase;
          min-height: 51px;
          min-width: 25px;
          max-width: 258px;
          width: 100%;
          padding: 0 1.5rem;
          margin-top: 30px;
        }
        @media (min-width: 480px) {
          .hero-cta { width: auto; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Property Name</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>In The Edge District</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
    'hero.rail_schedule': {
      label: 'Rail · Schedule a Visit',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_resident': {
      label: 'Rail · Resident Access',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_apply': {
      label: 'Rail · Apply Now',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_tour': {
      label: 'Rail · Virtual Tour',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_self_guided': {
      label: 'Rail · Self-Guided Tours',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'custom_self_guided_tour_url',
          suggestedFields: ['custom_self_guided_tour_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
